// frontend/src/components/planner/AddRecurringModal.jsx
// Create-mode modal for adding a new recurring bill/income/debt from the Forecast page
import React, { useState, useEffect } from 'react';
import { X, Calendar, DollarSign, Percent, CreditCard, Building2, Tag } from 'lucide-react';
import api from '../../utils/api';
import { useBusinessEntity } from '../../contexts/BusinessEntityContext';

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' }
];

const DEBT_SUBTYPES = [
  { value: 'credit_card', label: 'Credit Card', isRevolving: true },
  { value: 'auto_loan', label: 'Auto Loan', isRevolving: false },
  { value: 'student_loan', label: 'Student Loan', isRevolving: false },
  { value: 'mortgage', label: 'Mortgage', isRevolving: false },
  { value: 'personal_loan', label: 'Personal Loan', isRevolving: false },
  { value: 'medical_debt', label: 'Medical Debt', isRevolving: false },
  { value: 'other', label: 'Other', isRevolving: false }
];

function getTodayString() {
  return new Date().toISOString().split('T')[0];
}

export default function AddRecurringModal({ selectedAccountId, accountsList, onClose, onSuccess }) {
  const { activeFilter } = useBusinessEntity();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);

  // Form state
  const [name, setName] = useState('');
  const [itemType, setItemType] = useState('bill');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState('monthly');
  const [nextDueDate, setNextDueDate] = useState(getTodayString());
  const [dayOfMonth, setDayOfMonth] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [bankAccountId, setBankAccountId] = useState(selectedAccountId?.toString() || '');
  const [isVariable, setIsVariable] = useState(false);
  const [isEnvelope, setIsEnvelope] = useState(false);
  const [startDate, setStartDate] = useState(getTodayString());

  // Debt-specific fields
  const [debtSubtype, setDebtSubtype] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [remainingBalance, setRemainingBalance] = useState('');
  const [minimumPayment, setMinimumPayment] = useState('');
  const [originalBalance, setOriginalBalance] = useState('');
  const [maturityDate, setMaturityDate] = useState('');

  const isRevolvingDebt = DEBT_SUBTYPES.find(d => d.value === debtSubtype)?.isRevolving ?? false;

  // Load categories on mount
  useEffect(() => {
    async function loadCategories() {
      try {
        const response = await api.get('/api/household/categories');
        if (response.data.categories) {
          setCategories(response.data.categories);
        }
      } catch (err) {
        console.error('Error loading categories:', err);
      }
    }
    loadCategories();
  }, []);

  const typeConfig = {
    bill: { title: 'Add Recurring Bill', color: 'orange' },
    debt: { title: 'Add Recurring Debt', color: 'red' },
    income: { title: 'Add Recurring Income', color: 'emerald' }
  };

  // Static color mapping to avoid dynamic Tailwind class names (PurgeCSS strips them)
  const colorStyles = {
    orange: { headerBg: 'bg-orange-600', submitBg: 'bg-orange-600', submitHover: 'hover:bg-orange-700' },
    red: { headerBg: 'bg-red-600', submitBg: 'bg-red-600', submitHover: 'hover:bg-red-700' },
    emerald: { headerBg: 'bg-emerald-600', submitBg: 'bg-emerald-600', submitHover: 'hover:bg-emerald-700' },
  };

  const { title, color } = typeConfig[itemType] || typeConfig.bill;
  const styles = colorStyles[color] || colorStyles.emerald;

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (isVariable) {
      if (!startDate) {
        setError('Start date is required for variable items');
        return;
      }
    } else {
      if (!nextDueDate) {
        setError('Next due date is required');
        return;
      }
    }

    if (itemType === 'debt') {
      if (!debtSubtype) {
        setError('Please select a debt type');
        return;
      }
      const rate = parseFloat(interestRate);
      const balance = parseFloat(remainingBalance);
      if (isNaN(rate) || rate < 0) {
        setError('Please enter a valid interest rate');
        return;
      }
      if (isNaN(balance) || balance < 0) {
        setError('Please enter a valid remaining balance');
        return;
      }
    }

    try {
      setLoading(true);

      const payload = {
        name: name.trim(),
        item_type: itemType,
        amount: amountNum,
        frequency,
        is_variable: isVariable,
        is_envelope: itemType === 'bill' ? isEnvelope : false,
        next_due_date: isVariable ? null : nextDueDate,
        start_date: isVariable ? startDate : null,
        day_of_month: isVariable ? null : (dayOfMonth ? parseInt(dayOfMonth, 10) : null),
        category_id: categoryId ? parseInt(categoryId, 10) : null,
        bank_account_id: bankAccountId ? parseInt(bankAccountId, 10) : null,
        business_entity_id: activeFilter && activeFilter !== 'all' && activeFilter !== 'personal'
          ? activeFilter
          : null
      };

      // Add debt fields
      if (itemType === 'debt') {
        payload.interest_rate = parseFloat(interestRate) || 0;
        payload.remaining_balance = parseFloat(remainingBalance) || 0;
        payload.minimum_payment = parseFloat(minimumPayment) || amountNum;
        payload.debt_subtype = debtSubtype;
        payload.original_balance = originalBalance ? parseFloat(originalBalance) : null;
        payload.maturity_date = maturityDate || null;
      }

      const response = await api.post('/api/planner/recurring-items', payload);

      if (response.data.success) {
        onSuccess();
      } else {
        setError(response.data.error || 'Failed to create recurring item');
      }
    } catch (err) {
      console.error('Error creating recurring item:', err);
      setError(err.response?.data?.error || 'Failed to create recurring item');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
      <div className="fixed inset-0" onClick={onClose} />

      <div className="relative bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:mx-4 sm:rounded-xl rounded-t-2xl overflow-hidden flex flex-col shadow-xl">
        {/* Header */}
        <div className={`sticky top-0 ${styles.headerBg} px-4 sm:px-5 py-4 flex-shrink-0 z-10`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">{title}</h3>
              <p className="text-base sm:text-sm text-white/80">Add a new recurring series to your forecast</p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center -mr-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-base sm:text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Netflix, Car Payment, Salary"
              className="w-full px-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              autoFocus
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2">Type</label>
            <div className="flex gap-2">
              {[
                { value: 'bill', label: 'Bill', activeClass: 'border-blue-500 text-blue-600 bg-blue-50' },
                { value: 'debt', label: 'Debt', activeClass: 'border-orange-500 text-orange-600 bg-orange-50' },
                { value: 'income', label: 'Income', activeClass: 'border-green-500 text-green-600 bg-green-50' }
              ].map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setItemType(t.value)}
                  className={`flex-1 px-3 py-2.5 sm:py-2 rounded-lg text-base sm:text-sm font-medium transition-all border min-h-[44px] ${
                    itemType === t.value
                      ? t.activeClass
                      : 'border-gray-300 text-gray-500 hover:border-gray-400'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
              {itemType === 'debt' ? 'Payment Amount' : 'Amount'}
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onWheel={(e) => e.target.blur()}
                placeholder="0.00"
                className="w-full pl-9 pr-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>

          {/* Frequency */}
          <div>
            <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">Frequency</label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="w-full px-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              {FREQUENCIES.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>

          {/* Variable Timing Toggle */}
          <div className="flex items-center gap-3 py-1">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isVariable}
                onChange={(e) => setIsVariable(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
            </label>
            <div>
              <span className="text-base sm:text-sm font-medium text-gray-700">Variable timing</span>
              <p className="text-xs text-gray-500">Date isn't fixed (e.g., haircuts, groceries)</p>
            </div>
          </div>

          {/* Budget Envelope Toggle - only for bills */}
          {itemType === 'bill' && (
            <div className="flex items-center gap-3 py-1">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isEnvelope}
                  onChange={(e) => setIsEnvelope(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
              <div>
                <span className="text-base sm:text-sm font-medium text-gray-700">Budget envelope</span>
                <p className="text-xs text-gray-500">Track actual spending against this budget</p>
              </div>
            </div>
          )}

          {/* Next Due Date - only for fixed items */}
          {!isVariable && (
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">Next Due Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={nextDueDate}
                  onChange={(e) => setNextDueDate(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>
          )}

          {/* Start Date - only for variable items */}
          {isVariable && (
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">When to start including in projections</p>
            </div>
          )}

          {/* Day of Month for Monthly - only for fixed items */}
          {!isVariable && frequency === 'monthly' && (
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                Day of Month <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="number"
                min="1"
                max="31"
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(e.target.value)}
                onWheel={(e) => e.target.blur()}
                placeholder="e.g., 15"
                className="w-full px-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          )}

          {/* Category */}
          <div>
            <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-gray-400">(optional)</span>
            </label>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none bg-white"
              >
                <option value="">No category</option>
                {categories
                  .filter(cat => {
                    if (itemType === 'income') return cat.name === 'Income';
                    return cat.name !== 'Income';
                  })
                  .map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
              </select>
            </div>
          </div>

          {/* Bank Account */}
          <div>
            <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
              {itemType === 'income' ? 'Deposits To' : 'Paid From'} <span className="text-gray-400">(optional)</span>
            </label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={bankAccountId}
                onChange={(e) => setBankAccountId(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none bg-white"
              >
                <option value="">Select an account...</option>
                {(accountsList || []).map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.displayNameWithInstitution || acc.displayName}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Debt-specific fields */}
          {itemType === 'debt' && (
            <>
              <div className="border-t border-gray-200 pt-4 mt-2">
                <h4 className="text-base sm:text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Debt Details
                </h4>

                {/* Debt Type */}
                <div className="mb-3">
                  <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                    Debt Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={debtSubtype}
                    onChange={(e) => setDebtSubtype(e.target.value)}
                    className="w-full px-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="">Select debt type...</option>
                    {DEBT_SUBTYPES.map(dt => (
                      <option key={dt.value} value={dt.value}>{dt.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {isRevolvingDebt ? 'Revolving debt with compound interest' : debtSubtype ? 'Fixed-term loan with simple interest' : ''}
                  </p>
                </div>

                {/* Interest Rate */}
                <div className="mb-3">
                  <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                    Interest Rate (APR)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={interestRate}
                      onChange={(e) => setInterestRate(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      className="w-full pl-3 pr-8 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                    <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  </div>
                </div>

                {/* Remaining Balance */}
                <div className="mb-3">
                  <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                    Current Balance
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={remainingBalance}
                      onChange={(e) => setRemainingBalance(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      className="w-full pl-9 pr-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                </div>

                {/* Minimum Payment */}
                <div className="mb-3">
                  <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                    Minimum Payment
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={minimumPayment}
                      onChange={(e) => setMinimumPayment(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      className="w-full pl-9 pr-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                </div>

                {/* Installment Loan Fields */}
                {debtSubtype && !isRevolvingDebt && (
                  <div className="border-t border-gray-100 pt-3 mt-3">
                    <p className="text-xs text-gray-500 mb-2">Optional: Track loan progress</p>

                    <div className="mb-3">
                      <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                        Original Loan Amount
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={originalBalance}
                          onChange={(e) => setOriginalBalance(e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          placeholder="e.g., 25000.00"
                          className="w-full pl-9 pr-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-base sm:text-sm font-medium text-gray-700 mb-1">
                        Payoff Date
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="date"
                          value={maturityDate}
                          onChange={(e) => setMaturityDate(e.target.value)}
                          className="w-full pl-9 pr-3 py-2.5 sm:py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-3 sm:py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] text-base sm:text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`w-full sm:w-auto px-4 py-3 sm:py-2 ${styles.submitBg} text-white rounded-lg ${styles.submitHover} disabled:opacity-50 transition-colors min-h-[44px] text-base sm:text-sm font-medium`}
            >
              {loading ? 'Creating...' : 'Add to Forecast'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
